import Choices from 'choices.js';
import Rails from '@rails/ujs';
import SidePanel from './sidepanel';

// Spread a Choices.js v11 classname (string or string[]) onto a classList.
function applyClasses(el, classNames) {
  [].concat(classNames).forEach(c => c && el.classList.add(c));
}

const Tags = {
  start() {
    SidePanel.onUpdate($sidepanel => {
      const $tagsPicker = $sidepanel.find('.js-tags-picker');
      if ($tagsPicker.length > 0) {
        this.hookUpChoicesPlugin($tagsPicker[0]);
      }
    });
  },

  hookUpChoicesPlugin(element) {
    // searchEnabled is required for inline creation: Choices.js exposes the
    // typed value via the cloned input that only renders when search is on.
    const inlineCreateUrl = element.dataset.tagCreateUrl;

    // Register the inline-creation keydown listener BEFORE Choices.js
    // initializes.  Choices.js v11 binds its own keydown handler on the
    // outer .choices wrapper in capture phase
    // (outerElement.addEventListener('keydown', ..., true)), and same-
    // element same-phase listeners fire in registration order — Choices
    // would otherwise win.  Binding on the select's pre-init parent (one
    // node above the wrapper Choices is about to create) puts my listener
    // strictly higher in the capture chain, so it always fires first.
    let inlineCreationContext = null;
    if (inlineCreateUrl) {
      inlineCreationContext = this.attachInlineCreationListener(element, inlineCreateUrl);
    }

    const choices = new Choices(element, {
      searchEnabled: Boolean(inlineCreateUrl),
      searchResultLimit: 50,
      removeItemButton: true,
      // Choices.js v11: callbackOnCreateTemplates receives (strToEl, escapeForTemplate, getClassNames).
      // Each returned template function receives (TemplateOptions, ChoiceFull, ...).
      // TemplateOptions.classNames are string[] arrays in v11.
      // ChoiceFull.customProperties is already a parsed object (parseCustomProperties ran on mount).
      callbackOnCreateTemplates() {
        return {
          // Renders a selected "chip" item inside the input area.
          item(
            { classNames: { item, button, highlightedState, itemSelectable, placeholder } },
            choice,
            removeItemButton
          ) {
            const div = document.createElement('div');
            applyClasses(div, item);

            const labelSpan = document.createElement('span');
            labelSpan.className = 'tag__label';
            // Tag names are organizer-supplied and stored verbatim: render as
            // text so a name like `<img src=x onerror=...>` cannot become markup.
            labelSpan.textContent = choice.label;
            div.appendChild(labelSpan);

            Object.assign(div.dataset, {
              item: '',
              id: choice.id,
              value: choice.value,
            });

            // Persist customProperties on the rendered element so downstream
            // code can still read it if needed.
            if (choice.customProperties) {
              div.dataset.customProperties =
                typeof choice.customProperties === 'string'
                  ? choice.customProperties
                  : JSON.stringify(choice.customProperties);
            }

            if (choice.active) div.setAttribute('aria-selected', 'true');
            if (choice.disabled) div.setAttribute('aria-disabled', 'true');

            if (choice.placeholder) {
              applyClasses(div, placeholder);
              div.dataset.placeholder = '';
            }

            applyClasses(div, choice.highlighted ? highlightedState : itemSelectable);

            // Bobcat: colour the chip. In v11 customProperties is a parsed object.
            const props =
              choice.customProperties && typeof choice.customProperties === 'object'
                ? choice.customProperties : null;
            const color = props?.color;
            if (color) {
              div.classList.add('tag', `tag--${color}`);
              div.style.fontWeight = 'normal';
              if (props.textColor) div.style.color = props.textColor;
            }

            if (removeItemButton) {
              if (choice.disabled) {
                [].concat(itemSelectable).forEach(c => c && div.classList.remove(c));
              }
              div.dataset.deletable = '';

              const REMOVE_ITEM_TEXT = 'Remove item';
              const removeButton = document.createElement('button');
              removeButton.type = 'button';
              applyClasses(removeButton, button);
              removeButton.innerHTML = REMOVE_ITEM_TEXT;
              removeButton.setAttribute('aria-label', `${REMOVE_ITEM_TEXT}: '${choice.value}'`);
              removeButton.dataset.button = '';
              div.appendChild(removeButton);
            }

            return div;
          },

          // Renders a row in the dropdown list.
          choice(
            { classNames: { item, itemChoice, itemSelectable, selectedState, itemDisabled, placeholder } },
            choice,
            selectText
          ) {
            const div = document.createElement('div');

            if (choice.elementId) div.id = choice.elementId;

            applyClasses(div, item);
            applyClasses(div, itemChoice);

            // Bobcat: render label as a coloured tag span.
            const props =
              choice.customProperties && typeof choice.customProperties === 'object'
                ? choice.customProperties : null;
            const color = props?.color;
            const tag = document.createElement('span');
            // Text, not markup — see the item template above.
            tag.textContent = choice.label;
            tag.className = `tag${color ? ` tag--${color}` : ''}`;
            if (color) {
              tag.style.fontWeight = 'normal';
              if (props.textColor) tag.style.color = props.textColor;
            }
            div.appendChild(tag);

            if (choice.selected) applyClasses(div, selectedState);
            if (choice.placeholder) applyClasses(div, placeholder);

            div.setAttribute(
              'role',
              choice.group && choice.group.id > 0 ? 'treeitem' : 'option'
            );

            Object.assign(div.dataset, {
              choice: '',
              id: choice.id,
              value: choice.value,
              selectText,
            });

            if (choice.disabled) {
              applyClasses(div, itemDisabled);
              div.dataset.choiceDisabled = '';
              div.setAttribute('aria-disabled', 'true');
            } else {
              applyClasses(div, itemSelectable);
              div.dataset.choiceSelectable = '';
            }

            return div;
          },
        };
      },
    });

    if (inlineCreationContext) {
      inlineCreationContext.choices = choices;
    }
  },

  // Bind a capture-phase keydown listener on the select's pre-init parent
  // so that we always run BEFORE Choices.js's own keydown handler.  See
  // hookUpChoicesPlugin for why parent-of-element placement is required.
  // Returns a context object whose `choices` property is filled in by the
  // caller after `new Choices(...)` returns; we capture it lazily because
  // Choices is constructed AFTER this listener is attached.
  attachInlineCreationListener(selectEl, createUrl) {
    const parent = selectEl.parentElement;
    if (!parent) return null;

    const context = { choices: null, isCreating: false };

    parent.addEventListener('keydown', async (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target;
      if (!input || !input.classList || !input.classList.contains('choices__input')) return;

      const typed = (input.value || '').trim().replace(/\s+/g, ' ');
      if (!typed) return;

      // Always own Enter when there's typed text.  Stop Choices.js's handler
      // from running and stop the default form-submit on a bare button row.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const choices = context.choices;
      if (!choices || context.isCreating) return;

      // Exact case-insensitive match in the catalog → select that tag.
      const existing = Array.from(selectEl.options).find(
        opt => (opt.text || '').toLowerCase() === typed.toLowerCase()
      );
      if (existing) {
        choices.setChoiceByValue(existing.value);
        choices.clearInput();
        return;
      }

      context.isCreating = true;
      try {
        const response = await fetch(createUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': Rails.csrfToken(),
          },
          body: JSON.stringify({ tag: { name: typed } }),
        });
        if (!response.ok) return;

        const tag = await response.json();
        const value = String(tag.id);

        choices.setChoices(
          [{
            value,
            label: tag.name,
            customProperties: { color: tag.color, textColor: tag.text_color },
          }],
          'value',
          'label',
          false,
        );
        choices.setChoiceByValue(value);
        choices.clearInput();
      } catch (e) {
        // Swallow — leaves the user able to retry without losing typed text.
      } finally {
        context.isCreating = false;
      }
    }, true);

    return context;
  },
};

export default Tags;
